// C:\IntegraNube_WebStore\src\pages\ProductoDetalle.js
'use strict';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Link as RouterLink,
  useNavigate,
  useParams,
} from 'react-router-dom';

import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';

import {
  AddOutlined as AddIcon,
  ArrowBackOutlined as ArrowBackIcon,
  CheckCircleOutline as AvailableIcon,
  DirectionsCarOutlined as VehicleIcon,
  ImageNotSupportedOutlined as NoImageIcon,
  NavigateNextOutlined as NavigateNextIcon,
  OpenInNewOutlined as OpenInNewIcon,
  RemoveOutlined as RemoveIcon,
  SellOutlined as BrandIcon,
  ShoppingCartOutlined as CartIcon,
  StarRounded as StarIcon,
} from '@mui/icons-material';

import api from '../api/api';
import mercadoLibreIcon from '../assets/mercadolibre.png';

/* ===================================================================== */
/*  Carrito WebStore                                                     */
/* ===================================================================== */

const CART_STORAGE_KEY = 'integranube_webstore_cart';
const CART_UPDATED_EVENT = 'integranube-cart-updated';

function readCart() {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw =
      window.localStorage.getItem(
        CART_STORAGE_KEY
      );

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);

    return Array.isArray(parsed)
      ? parsed
      : [];
  } catch (error) {
    console.warn(
      '[ProductoDetalle] No fue posible leer el carrito:',
      error
    );

    return [];
  }
}

function writeCart(items) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    CART_STORAGE_KEY,
    JSON.stringify(items)
  );

  window.dispatchEvent(
    new CustomEvent(
      CART_UPDATED_EVENT,
      {
        detail: {
          items,
        },
      }
    )
  );
}

function getCartItemQuantity(item) {
  const value =
    item?.cantidad ??
    item?.quantity ??
    0;

  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : 0;
}

function sameProduct(item, product) {
  if (!item || !product) {
    return false;
  }

  if (
    item.id !== undefined &&
    item.id !== null &&
    product.id !== undefined &&
    product.id !== null
  ) {
    return (
      String(item.id) ===
      String(product.id)
    );
  }

  const itemSku =
    String(item.sku || '').trim();

  const productSku =
    String(product.sku || '').trim();

  return Boolean(
    itemSku &&
    productSku &&
    itemSku === productSku
  );
}

/* ===================================================================== */
/*  Helpers tienda                                                       */
/* ===================================================================== */

function resolveTiendaData(source) {
  if (!source || typeof source !== 'object') {
    return {};
  }

  if (
    source.status &&
    source.data &&
    typeof source.data === 'object'
  ) {
    return source.data;
  }

  if (
    source.data &&
    typeof source.data === 'object' &&
    (
      source.data.branding ||
      source.data.nombre
    )
  ) {
    return source.data;
  }

  return source;
}

function safeColor(value, fallback) {
  const color =
    String(value || '').trim();

  return color || fallback;
}

/* ===================================================================== */
/*  Helpers producto                                                     */
/* ===================================================================== */

function extractProduct(payload) {
  if (
    !payload ||
    typeof payload !== 'object'
  ) {
    return null;
  }

  if (
    payload.data &&
    typeof payload.data === 'object' &&
    !Array.isArray(payload.data)
  ) {
    return payload.data;
  }

  if (
    payload.producto &&
    typeof payload.producto === 'object'
  ) {
    return payload.producto;
  }

  return payload;
}

function formatPrice(
  value,
  moneda = 'CLP'
) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return null;
  }

  try {
    return new Intl.NumberFormat(
      'es-CL',
      {
        style: 'currency',
        currency:
          moneda || 'CLP',
        maximumFractionDigits:
          moneda === 'CLP'
            ? 0
            : 2,
      }
    ).format(number);
  } catch (_) {
    return (
      '$' +
      Math.round(number)
        .toLocaleString('es-CL')
    );
  }
}

function formatUnit(
  value,
  pesable = false
) {
  const raw =
    String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(
        /[\u0300-\u036f]/g,
        ''
      );

  if (!raw && pesable) {
    return 'Kilogramo';
  }

  if (!raw) {
    return 'Unidad';
  }

  const map = {
    un: 'Unidad',
    und: 'Unidad',
    u: 'Unidad',
    uni: 'Unidad',
    unidad: 'Unidad',
    unidades: 'Unidad',

    kg: 'Kilogramo',
    kgs: 'Kilogramo',
    kilo: 'Kilogramo',
    kilos: 'Kilogramo',
    kilogramo: 'Kilogramo',
    kilogramos: 'Kilogramo',

    g: 'Gramo',
    gr: 'Gramo',
    grs: 'Gramo',
    gramo: 'Gramo',
    gramos: 'Gramo',

    l: 'Litro',
    lt: 'Litro',
    lts: 'Litro',
    litro: 'Litro',
    litros: 'Litro',

    ml: 'Mililitro',
    mililitro: 'Mililitro',
    mililitros: 'Mililitro',

    m: 'Metro',
    mt: 'Metro',
    mts: 'Metro',
    metro: 'Metro',
    metros: 'Metro',

    cm: 'Centímetro',
    centimetro: 'Centímetro',
    centimetros: 'Centímetro',

    caja: 'Caja',
    cajas: 'Caja',

    paquete: 'Paquete',
    paquetes: 'Paquete',

    pack: 'Pack',

    bolsa: 'Bolsa',
    bolsas: 'Bolsa',

    bot: 'Botella',
    botella: 'Botella',
    botellas: 'Botella',
  };

  if (map[raw]) {
    return map[raw];
  }

  if (
    pesable &&
    [
      'unidad',
      'un',
      'und',
      'u',
    ].includes(raw)
  ) {
    return 'Kilogramo';
  }

  return (
    raw.charAt(0).toUpperCase() +
    raw.slice(1)
  );
}

function getProductPriceInfo(product) {
  const moneda =
    product?.moneda || 'CLP';

  const precio = Number(
    product?.precio ??
    product?.precio_bruto ??
    product?.precioDetalle?.bruto ??
    product?.precioNeto ??
    product?.precio_neto
  );

  const precioNormal = Number(
    product?.precioNormal ??
    product?.precioNormalDetalle?.bruto
  );

  const ofertaDeclarada =
    product?.enOferta === true ||
    product?.oferta?.activa === true;

  const enOferta =
    ofertaDeclarada &&
    Number.isFinite(precio) &&
    precio > 0 &&
    Number.isFinite(precioNormal) &&
    precioNormal > precio;

  const ahorro = enOferta
    ? Math.max(
        0,
        precioNormal - precio
      )
    : 0;

  const descuentoPorcentaje =
    enOferta && precioNormal > 0
      ? (ahorro / precioNormal) * 100
      : 0;

  return {
    moneda,
    precio:
      Number.isFinite(precio)
        ? precio
        : null,
    precioNormal:
      enOferta
        ? precioNormal
        : null,
    enOferta,
    ahorro,
    descuentoPorcentaje,
  };
}

function formatDiscountPercentage(value) {
  const number = Number(value);

  if (!Number.isFinite(number) || number <= 0) {
    return null;
  }

  if (number >= 10) {
    return Math.round(number).toLocaleString('es-CL');
  }

  if (number >= 1) {
    return number.toLocaleString('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });
  }

  return number.toLocaleString('es-CL', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });
}

function normalizeImages(product) {
  const result = [];

  const pushImage = (value) => {
    const image =
      String(value || '').trim();

    if (
      image &&
      !result.includes(image)
    ) {
      result.push(image);
    }
  };

  pushImage(product?.imagen);
  pushImage(product?.imagen_url);
  pushImage(product?.imagenUrl);

  if (
    Array.isArray(
      product?.imagenes
    )
  ) {
    product.imagenes.forEach(
      (item) => {
        if (
          typeof item === 'string'
        ) {
          pushImage(item);
          return;
        }

        if (
          item &&
          typeof item === 'object'
        ) {
          pushImage(
            item.url ||
            item.url_publica ||
            item.src ||
            item.imagen
          );
        }
      }
    );
  }

  return result;
}

function cleanText(value) {
  const text =
    String(value || '').trim();

  return text || null;
}

function normalizeMercadoLibreUrl(value) {
  const url = String(value || '').trim();

  if (!url) {
    return null;
  }

  if (/^http:\/\/articulo\.mercadolibre\.cl\//i.test(url)) {
    return url.replace(/^http:/i, 'https:');
  }

  return url;
}

function getMercadoLibreChannel(product) {
  const channel =
    product?.canalesVenta?.mercadolibre;

  const url = normalizeMercadoLibreUrl(
    channel?.url
  );

  if (
    channel?.disponible !== true ||
    !url
  ) {
    return null;
  }

  return {
    disponible: true,
    url,
  };
}

/* ===================================================================== */
/*  Helpers compatibilidad vehicular                                     */
/* ===================================================================== */

function formatCompatibilityYears(item) {
  const explicitYears = Array.isArray(item?.anios)
    ? [...new Set(
        item.anios
          .map((year) => Number.parseInt(year, 10))
          .filter((year) => Number.isInteger(year) && year > 1900)
      )].sort((a, b) => a - b)
    : [];

  if (explicitYears.length > 0) {
    const first = explicitYears[0];
    const last = explicitYears[explicitYears.length - 1];
    const contiguous =
      explicitYears.length === last - first + 1;

    return contiguous && first !== last
      ? `${first}–${last}`
      : explicitYears.join(', ');
  }

  const from = Number.parseInt(item?.anioDesde, 10);
  const to = Number.parseInt(item?.anioHasta, 10);
  const hasFrom = Number.isInteger(from) && from > 1900;
  const hasTo = Number.isInteger(to) && to > 1900;

  if (hasFrom && hasTo) {
    return from === to
      ? String(from)
      : `${from}–${to}`;
  }

  if (hasFrom) return `Desde ${from}`;
  if (hasTo) return `Hasta ${to}`;
  return '';
}

function formatEngineDisplacement(value) {
  const cc = Number(value);

  if (!Number.isFinite(cc) || cc <= 0) {
    return '';
  }

  if (cc >= 1000) {
    const liters = cc / 1000;

    return `${liters.toLocaleString('es-CL', {
      minimumFractionDigits: Number.isInteger(liters) ? 0 : 1,
      maximumFractionDigits: 1,
    })} L`;
  }

  return `${Math.round(cc).toLocaleString('es-CL')} cc`;
}

function normalizeCompatibilityGroups(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  const groups = new Map();

  items.forEach((item, index) => {
    if (!item || typeof item !== 'object') {
      return;
    }

    const brand =
      cleanText(item?.marca?.nombre) ||
      'Marca no especificada';

    const model =
      cleanText(item?.modelo?.nombre) ||
      'Modelo no especificado';

    const key = `${brand.toLowerCase()}::${model.toLowerCase()}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        marca: brand,
        modelo: model,
        aplicaciones: [],
      });
    }

    const engineCode =
      cleanText(item?.motor?.codigo);

    const engineDisplacement =
      formatEngineDisplacement(
        item?.motor?.cilindradaCc
      );

    const fuel = cleanText(
      item?.motor?.combustible
    );

    const engineParts = [
      engineCode,
      engineDisplacement,
      fuel
        ? fuel.charAt(0).toUpperCase() +
          fuel.slice(1).toLowerCase()
        : null,
    ].filter(Boolean);

    groups.get(key).aplicaciones.push({
      key:
        item?.id ??
        item?.versionId ??
        `${key}-${index}`,
      codigoModelo:
        cleanText(item?.codigoModelo),
      anios:
        formatCompatibilityYears(item),
      version:
        cleanText(item?.nombreVersion),
      motor:
        engineParts.join(' · '),
      posicion:
        cleanText(item?.posicion),
      cantidadPorVehiculo:
        Number.isFinite(
          Number(item?.cantidadPorVehiculo)
        )
          ? Number(item.cantidadPorVehiculo)
          : null,
      restricciones:
        cleanText(item?.restricciones),
      observaciones:
        cleanText(item?.observaciones),
    });
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      aplicaciones: group.aplicaciones.sort((a, b) => {
        const aYear = Number.parseInt(a.anios, 10) || 9999;
        const bYear = Number.parseInt(b.anios, 10) || 9999;

        if (aYear !== bYear) {
          return aYear - bYear;
        }

        return String(a.codigoModelo || '').localeCompare(
          String(b.codigoModelo || ''),
          'es',
          { sensitivity: 'base' }
        );
      }),
    }))
    .sort((a, b) =>
      `${a.marca} ${a.modelo}`.localeCompare(
        `${b.marca} ${b.modelo}`,
        'es',
        { sensitivity: 'base' }
      )
    );
}

/* ===================================================================== */
/*  ProductoDetalle                                                      */
/* ===================================================================== */

export default function ProductoDetalle({
  tienda = null,
}) {
  const { slug } = useParams();
  const navigate = useNavigate();

  /*
   * La URL pública/canónica definitiva es /producto/{slug}.
   * Las URLs históricas /{slug} se resuelven con 301 en Cloudflare
   * antes de llegar a React.
   */
  const identificadorProducto =
    slug || '';

  const tiendaData =
    useMemo(
      () =>
        resolveTiendaData(tienda),
      [tienda]
    );

  const colorPrimario =
    safeColor(
      tiendaData?.branding
        ?.colorPrimario,
      '#1976d2'
    );

  const mostrarStock =
    tiendaData?.ecommerce
      ?.mostrarStock !== false;

  const mostrarSku =
    tiendaData?.ecommerce
      ?.mostrarSku !== false;

  const carritoHabilitado =
    tiendaData?.funciones
      ?.carrito !== false;

  const [
    producto,
    setProducto,
  ] = useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  const [
    selectedImage,
    setSelectedImage,
  ] = useState('');

  const [
    imageError,
    setImageError,
  ] = useState(false);

  const [
    cantidadAgregar,
    setCantidadAgregar,
  ] = useState(1);

  const [
    cantidadEnCarrito,
    setCantidadEnCarrito,
  ] = useState(0);

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    let mounted = true;

    const controller =
      new AbortController();

    async function loadProduct() {
      setLoading(true);
      setError('');

      try {
        const response =
          await api.get(
            `/productos/${encodeURIComponent(
              identificadorProducto
            )}`,
            {
              signal:
                controller.signal,
            }
          );

        if (!mounted) {
          return;
        }

        const data =
          extractProduct(
            response?.data
          );

        if (
          !data ||
          typeof data !== 'object'
        ) {
          throw new Error(
            'Producto no encontrado'
          );
        }

        setProducto(data);

        const images =
          normalizeImages(data);

        setSelectedImage(
          images[0] || ''
        );

        setImageError(false);
      } catch (
        requestError
      ) {
        if (
          requestError?.name ===
            'CanceledError' ||
          requestError?.name ===
            'AbortError' ||
          requestError?.code ===
            'ERR_CANCELED'
        ) {
          return;
        }

        console.error(
          '[ProductoDetalle] Error:',
          requestError
        );

        if (mounted) {
          setProducto(null);

          setError(
            requestError
              ?.response
              ?.status === 404
              ? 'El producto no fue encontrado.'
              : 'No fue posible cargar el producto.'
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadProduct();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [identificadorProducto]);

  const refreshCartState =
    useCallback(() => {
      if (!producto) {
        setCantidadEnCarrito(0);
        return;
      }

      const cart =
        readCart();

      const item =
        cart.find((row) =>
          sameProduct(
            row,
            producto
          )
        );

      setCantidadEnCarrito(
        item
          ? getCartItemQuantity(
              item
            )
          : 0
      );
    }, [producto]);

  useEffect(() => {
    refreshCartState();

    const handleUpdate = () => {
      refreshCartState();
    };

    const handleStorage = (
      event
    ) => {
      if (
        !event.key ||
        event.key ===
          CART_STORAGE_KEY
      ) {
        refreshCartState();
      }
    };

    window.addEventListener(
      CART_UPDATED_EVENT,
      handleUpdate
    );

    window.addEventListener(
      'storage',
      handleStorage
    );

    return () => {
      window.removeEventListener(
        CART_UPDATED_EVENT,
        handleUpdate
      );

      window.removeEventListener(
        'storage',
        handleStorage
      );
    };
  }, [
    producto,
    refreshCartState,
  ]);

  useEffect(() => {
    setImageError(false);
  }, [selectedImage]);

  const stockValue =
    Number(producto?.stock);

  const stockKnown =
    producto?.stock !== null &&
    producto?.stock !== undefined &&
    Number.isFinite(
      stockValue
    );

  const maxAgregar =
    stockKnown
      ? Math.max(
          0,
          Math.floor(
            stockValue -
            cantidadEnCarrito
          )
        )
      : null;

  const decreaseQuantity = () => {
    setCantidadAgregar(
      (current) =>
        Math.max(
          1,
          Number(current || 1) -
            1
        )
    );
  };

  const increaseQuantity = () => {
    setCantidadAgregar(
      (current) => {
        const next =
          Math.max(
            1,
            Number(
              current || 1
            )
          ) + 1;

        if (
          maxAgregar !== null &&
          next > maxAgregar
        ) {
          return current;
        }

        return next;
      }
    );
  };

  const agregarAlCarrito = () => {
    if (
      !producto ||
      !carritoHabilitado
    ) {
      return;
    }

    if (
      producto.disponible ===
      false
    ) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message:
          'Este producto no está disponible.',
      });

      return;
    }

    const cantidad =
      Math.max(
        1,
        Math.floor(
          Number(
            cantidadAgregar
          ) || 1
        )
      );

    const cart =
      readCart();

    const index =
      cart.findIndex(
        (row) =>
          sameProduct(
            row,
            producto
          )
      );

    const actual =
      index >= 0
        ? getCartItemQuantity(
            cart[index]
          )
        : 0;

    const nuevaCantidad =
      actual + cantidad;

    if (
      stockKnown &&
      nuevaCantidad >
        stockValue
    ) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message:
          `No puedes agregar más de ${stockValue.toLocaleString(
            'es-CL'
          )} unidades disponibles.`,
      });

      return;
    }

    const imagenes =
      normalizeImages(
        producto
      );

    const cartItem = {
      ...producto,

      id: producto.id,
      sku:
        producto.sku ||
        null,

      nombre:
        producto.nombre ||
        'Producto',

      precio:
        Number(
          producto.precio ??
          producto.precio_bruto ??
          producto.precioNeto ??
          producto.precio_neto ??
          0
        ),

      moneda:
        producto.moneda ||
        'CLP',

      unidad:
        producto.unidad ||
        'unidad',

      pesable:
        producto.pesable ===
        true,

      imagen:
        producto.imagen ||
        imagenes[0] ||
        null,

      imagenes,

      cantidad:
        nuevaCantidad,

      quantity:
        nuevaCantidad,
    };

    const nextCart =
      [...cart];

    if (index >= 0) {
      nextCart[index] =
        cartItem;
    } else {
      nextCart.push(
        cartItem
      );
    }

    writeCart(nextCart);

    setCantidadEnCarrito(
      nuevaCantidad
    );

    setCantidadAgregar(1);

    setSnackbar({
      open: true,
      severity: 'success',
      message:
        cantidad === 1
          ? `${producto.nombre} agregado al carrito.`
          : `${cantidad} unidades de ${producto.nombre} agregadas al carrito.`,
    });
  };

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          py: {
            xs: 3,
            md: 5,
          },
        }}
      >
        <Skeleton
          width={220}
          height={24}
          sx={{ mb: 2 }}
        />

        <Paper
          elevation={0}
          sx={{
            p: {
              xs: 2,
              md: 3,
            },
            border:
              '1px solid #e5e7eb',
            borderRadius: 3,
          }}
        >
          <Grid
            container
            spacing={{
              xs: 3,
              md: 4,
            }}
          >
            <Grid
              item
              xs={12}
              md={5}
            >
              <Skeleton
                variant="rounded"
                sx={{
                  width:
                    '100%',
                  maxWidth: 390,
                  aspectRatio:
                    '1 / 1',
                  mx: 'auto',
                  borderRadius: 3,
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={7}
            >
              <Skeleton
                width={100}
              />

              <Skeleton
                width="80%"
                height={48}
              />

              <Skeleton
                width={210}
                height={46}
              />

              <Skeleton
                width="100%"
                height={120}
              />
            </Grid>
          </Grid>
        </Paper>
      </Container>
    );
  }

  if (
    error ||
    !producto
  ) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          py: {
            xs: 4,
            md: 6,
          },
        }}
      >
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
        >
          {error ||
            'Producto no disponible.'}
        </Alert>

        <Button
          component={RouterLink}
          to="/productos"
          startIcon={
            <ArrowBackIcon />
          }
          sx={{
            textTransform:
              'none',
            fontWeight: 800,
          }}
        >
          Volver a productos
        </Button>
      </Container>
    );
  }

  const nombre =
    cleanText(
      producto.nombre
    ) ||
    'Producto';

  const descripcion =
    cleanText(
      producto.descripcion
    );

  const sku =
    cleanText(
      producto.sku
    );

  const priceInfo =
    getProductPriceInfo(producto);

  const precio =
    formatPrice(
      priceInfo.precio,
      priceInfo.moneda
    );

  const precioNormal =
    priceInfo.enOferta
      ? formatPrice(
          priceInfo.precioNormal,
          priceInfo.moneda
        )
      : null;

  const ahorro =
    priceInfo.enOferta
      ? formatPrice(
          priceInfo.ahorro,
          priceInfo.moneda
        )
      : null;

  const descuentoPorcentaje =
    formatDiscountPercentage(
      priceInfo.descuentoPorcentaje
    );

  const unidad =
    formatUnit(
      producto.unidad,
      producto.pesable ===
        true
    );

  const images =
    normalizeImages(
      producto
    );

  const marcaNombre =
    cleanText(
      producto?.marca?.nombre ||
      producto?.marca_nombre
    );

  /*
   * Marca pública WebStore.
   *
   * Store API puede entregar slug/logo dentro del objeto marca o, por
   * compatibilidad, como campos planos. El slug habilita la navegación a
   * /marca/{slug}; si no existe, la marca se conserva sólo informativa.
   */
  const marcaSlug =
    cleanText(
      producto?.marca?.slug ||
      producto?.marca_slug ||
      producto?.marcaSlug
    );

  const marcaLogo =
    cleanText(
      producto?.marca?.logo ||
      producto?.marca_logo ||
      producto?.marcaLogo ||
      producto?.logo_marca
    );

  const marcaRuta =
    marcaSlug
      ? `/marca/${encodeURIComponent(
          marcaSlug
        )}`
      : null;

  const categorias =
    Array.isArray(
      producto.categorias
    )
      ? producto.categorias
      : [];

  /*
   * Contexto de navegación de categoría.
   *
   * Un producto puede pertenecer a más de una categoría. Para el breadcrumb
   * usamos la primera categoría pública que tenga slug; todas las categorías
   * siguen mostrándose más abajo como chips navegables.
   */
  const categoriaNavegacion =
    categorias.find((categoria) => {
      if (!categoria || typeof categoria !== 'object') {
        return false;
      }

      return Boolean(
        cleanText(categoria.slug) &&
        cleanText(categoria.nombre)
      );
    }) || null;

  const categoriaNavegacionNombre =
    cleanText(
      categoriaNavegacion?.nombre
    );

  const categoriaNavegacionSlug =
    cleanText(
      categoriaNavegacion?.slug
    );

  const categoriaNavegacionRuta =
    categoriaNavegacionSlug
      ? `/categoria/${encodeURIComponent(
          categoriaNavegacionSlug
        )}`
      : '/categorias';

  const disponible =
    producto.disponible !==
    false;

  const destacado =
    producto.destacado ===
    true;

  const tieneEnCarrito =
    cantidadEnCarrito > 0;

  const puedeAgregar =
    disponible &&
    (
      maxAgregar === null ||
      maxAgregar > 0
    );

  const mercadolibre =
    getMercadoLibreChannel(
      producto
    );

  const compatibilidades =
    Array.isArray(producto?.compatibilidades)
      ? producto.compatibilidades
      : [];

  const compatibilidadGrupos =
    normalizeCompatibilityGroups(
      compatibilidades
    );

  const compatibilidadFilas =
    compatibilidadGrupos.flatMap(
      (group) =>
        group.aplicaciones.map(
          (application) => ({
            ...application,
            marca: group.marca,
            modelo: group.modelo,
          })
        )
    );

  const compatibilidadTotal =
    Number.isFinite(
      Number(
        producto?.compatibilidadResumen
          ?.total
      )
    )
      ? Number(
          producto.compatibilidadResumen.total
        )
      : compatibilidades.length;

  return (
    <Box
      sx={{
        bgcolor: '#f8fafc',
        minHeight:
          'calc(100vh - 80px)',
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          py: {
            xs: 2.5,
            md: 4,
          },
        }}
      >
        <Breadcrumbs
          separator={
            <NavigateNextIcon
              fontSize="small"
            />
          }
          sx={{
            mb: {
              xs: 2,
              md: 2.5,
            },
            color:
              '#6b7280',
          }}
        >
          <Typography
            component={
              RouterLink
            }
            to="/"
            sx={{
              color:
                'inherit',
              textDecoration:
                'none',
              fontSize: 14,

              '&:hover': {
                color:
                  colorPrimario,
              },
            }}
          >
            Inicio
          </Typography>

          <Typography
            component={
              RouterLink
            }
            to="/categorias"
            sx={{
              color:
                'inherit',
              textDecoration:
                'none',
              fontSize: 14,

              '&:hover': {
                color:
                  colorPrimario,
              },
            }}
          >
            Categorías
          </Typography>

          {categoriaNavegacionNombre ? (
            <Typography
              component={
                RouterLink
              }
              to={categoriaNavegacionRuta}
              sx={{
                color:
                  'inherit',
                textDecoration:
                  'none',
                fontSize: 14,
                fontWeight: 600,
                maxWidth: {
                  xs: 120,
                  sm: 220,
                },
                overflow:
                  'hidden',
                textOverflow:
                  'ellipsis',
                whiteSpace:
                  'nowrap',

                '&:hover': {
                  color:
                    colorPrimario,
                },
              }}
            >
              {categoriaNavegacionNombre}
            </Typography>
          ) : null}

          <Typography
            sx={{
              color:
                '#111827',
              fontSize: 14,
              fontWeight: 700,
              maxWidth: {
                xs: 150,
                sm: 320,
              },
              overflow:
                'hidden',
              textOverflow:
                'ellipsis',
              whiteSpace:
                'nowrap',
            }}
          >
            {nombre}
          </Typography>
        </Breadcrumbs>

        <Paper
          elevation={0}
          sx={{
            p: {
              xs: 2,
              sm: 2.5,
              md: 3,
            },
            border:
              '1px solid #e5e7eb',
            borderRadius: 3,
            bgcolor: '#fff',
          }}
        >
          <Grid
            container
            spacing={{
              xs: 3,
              md: 4,
            }}
            alignItems="flex-start"
          >
            <Grid
              item
              xs={12}
              md={5}
            >
              <Box
                sx={{
                  width:
                    '100%',
                  maxWidth: {
                    xs: 310,
                    sm: 350,
                    md: 390,
                  },
                  mx: {
                    xs: 'auto',
                    md: 0,
                  },
                }}
              >
                <Box
                  sx={{
                    position:
                      'relative',
                    width: '100%',
                    aspectRatio:
                      '1 / 1',
                    maxHeight: 390,
                    border:
                      '1px solid #eef2f7',
                    borderRadius: 3,
                    bgcolor:
                      '#fafafa',
                    display:
                      'grid',
                    placeItems:
                      'center',
                    overflow:
                      'hidden',
                  }}
                >
                  {selectedImage &&
                  !imageError ? (
                    <Box
                      component="img"
                      src={
                        selectedImage
                      }
                      alt={nombre}
                      loading="eager"
                      decoding="async"
                      onError={() =>
                        setImageError(
                          true
                        )
                      }
                      sx={{
                        display:
                          'block',
                        width:
                          '100%',
                        height:
                          '100%',
                        objectFit:
                          'contain',
                        p: {
                          xs: 1,
                          md: 1.5,
                        },
                      }}
                    />
                  ) : (
                    <Stack
                      spacing={1}
                      alignItems="center"
                      sx={{
                        color:
                          '#9ca3af',
                      }}
                    >
                      <NoImageIcon
                        sx={{
                          fontSize: 54,
                          opacity: 0.5,
                        }}
                      />

                      <Typography
                        variant="body2"
                      >
                        Sin imagen
                      </Typography>
                    </Stack>
                  )}

                  {destacado ? (
                    <Chip
                      icon={
                        <StarIcon
                          sx={{
                            fontSize:
                              '17px !important',
                          }}
                        />
                      }
                      label="Destacado"
                      size="small"
                      sx={{
                        position:
                          'absolute',
                        top: 12,
                        left: 12,
                        zIndex: 2,
                        bgcolor: '#fff',
                        color:
                          colorPrimario,
                        fontWeight:
                          800,
                        border:
                          `1px solid ${colorPrimario}30`,
                        boxShadow:
                          '0 4px 14px rgba(15,23,42,0.08)',
                      }}
                    />
                  ) : null}

                  {marcaLogo ? (
                    <Box
                      component={
                        marcaRuta
                          ? RouterLink
                          : 'div'
                      }
                      to={
                        marcaRuta ||
                        undefined
                      }
                      aria-label={
                        marcaRuta &&
                        marcaNombre
                          ? `Ver productos de ${marcaNombre}`
                          : undefined
                      }
                      title={
                        marcaNombre ||
                        'Marca'
                      }
                      sx={{
                        position:
                          'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 2,
                        width: {
                          xs: 72,
                          sm: 82,
                        },
                        height: {
                          xs: 38,
                          sm: 42,
                        },
                        px: 0.8,
                        py: 0.55,
                        display: 'grid',
                        placeItems:
                          'center',
                        borderRadius:
                          1.7,
                        bgcolor:
                          'rgba(255,255,255,0.96)',
                        border:
                          '1px solid rgba(226,232,240,0.95)',
                        boxShadow:
                          '0 5px 16px rgba(15,23,42,0.10)',
                        textDecoration:
                          'none',
                        transition:
                          'transform 160ms ease, box-shadow 160ms ease',
                        cursor:
                          marcaRuta
                            ? 'pointer'
                            : 'default',

                        ...(marcaRuta
                          ? {
                              '&:hover': {
                                transform:
                                  'translateY(-1px)',
                                boxShadow:
                                  '0 8px 20px rgba(15,23,42,0.14)',
                              },
                            }
                          : {}),
                      }}
                    >
                      <Box
                        component="img"
                        src={marcaLogo}
                        alt={
                          marcaNombre
                            ? `Logo ${marcaNombre}`
                            : 'Logo de marca'
                        }
                        loading="eager"
                        decoding="async"
                        onError={(event) => {
                          const container =
                            event.currentTarget
                              .parentElement;

                          if (container) {
                            container.style.display =
                              'none';
                          }
                        }}
                        sx={{
                          display:
                            'block',
                          maxWidth:
                            '100%',
                          maxHeight:
                            '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit:
                            'contain',
                        }}
                      />
                    </Box>
                  ) : null}
                </Box>

                {images.length >
                1 ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{
                      mt: 1.25,
                    }}
                  >
                    {images.map(
                      (
                        image,
                        index
                      ) => {
                        const active =
                          image ===
                          selectedImage;

                        return (
                          <IconButton
                            key={`${image}-${index}`}
                            onClick={() =>
                              setSelectedImage(
                                image
                              )
                            }
                            aria-label={`Ver imagen ${
                              index + 1
                            }`}
                            sx={{
                              width: 58,
                              height: 58,
                              p: 0.5,
                              border:
                                active
                                  ? `2px solid ${colorPrimario}`
                                  : '1px solid #e5e7eb',
                              borderRadius:
                                1.5,
                              bgcolor:
                                '#fff',
                            }}
                          >
                            <Box
                              component="img"
                              src={image}
                              alt=""
                              sx={{
                                width:
                                  '100%',
                                height:
                                  '100%',
                                objectFit:
                                  'contain',
                              }}
                            />
                          </IconButton>
                        );
                      }
                    )}
                  </Stack>
                ) : null}
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={7}
            >
              <Stack
                spacing={2}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  alignItems="center"
                >
                  {marcaNombre ? (
                    marcaRuta ? (
                      <Button
                        component={RouterLink}
                        to={marcaRuta}
                        variant="outlined"
                        size="small"
                        startIcon={
                          <BrandIcon
                            sx={{
                              fontSize:
                                '18px !important',
                            }}
                          />
                        }
                        endIcon={
                          <NavigateNextIcon
                            sx={{
                              fontSize:
                                '18px !important',
                            }}
                          />
                        }
                        aria-label={`Ver productos de ${marcaNombre}`}
                        sx={{
                          minHeight: 36,
                          px: 1.45,
                          borderRadius: 999,
                          borderWidth: 1.5,
                          borderColor:
                            `${colorPrimario}65`,
                          bgcolor:
                            `${colorPrimario}0D`,
                          color:
                            colorPrimario,
                          fontWeight: 900,
                          fontSize: 13.5,
                          lineHeight: 1,
                          textTransform:
                            'none',
                          textDecoration:
                            'none',
                          boxShadow:
                            '0 4px 12px rgba(15,23,42,0.06)',
                          transition:
                            'transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease, border-color 160ms ease',

                          '&:hover': {
                            transform:
                              'translateY(-1px)',
                            borderColor:
                              colorPrimario,
                            bgcolor:
                              `${colorPrimario}18`,
                            boxShadow:
                              '0 7px 18px rgba(15,23,42,0.10)',
                          },

                          '& .MuiButton-startIcon': {
                            mr: 0.7,
                          },

                          '& .MuiButton-endIcon': {
                            ml: 0.35,
                          },
                        }}
                      >
                        Marca: {marcaNombre}
                      </Button>
                    ) : (
                      <Chip
                        icon={
                          <BrandIcon
                            sx={{
                              fontSize:
                                '17px !important',
                            }}
                          />
                        }
                        label={`Marca: ${marcaNombre}`}
                        size="small"
                        sx={{
                          minHeight: 34,
                          bgcolor:
                            '#f8fafc',
                          color:
                            '#475569',
                          border:
                            '1px solid #e2e8f0',
                          fontWeight: 800,
                          '& .MuiChip-icon': {
                            color:
                              '#64748b',
                          },
                        }}
                      />
                    )
                  ) : null}

                  {mostrarSku &&
                  sku ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          '#6b7280',
                        fontWeight:
                          700,
                      }}
                    >
                      SKU {sku}
                    </Typography>
                  ) : null}
                </Stack>

                <Typography
                  component="h1"
                  sx={{
                    color:
                      '#111827',
                    fontWeight: 900,
                    fontSize: {
                      xs: 27,
                      sm: 32,
                      md: 36,
                    },
                    lineHeight: 1.12,
                    letterSpacing:
                      '-0.035em',
                  }}
                >
                  {nombre}
                </Typography>

                {precio ? (
                  <Box>
                    {priceInfo.enOferta &&
                    precioNormal ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                        alignItems="center"
                        sx={{ mb: 0.8 }}
                      >
                        <Typography
                          sx={{
                            color:
                              '#9ca3af',
                            fontWeight:
                              700,
                            fontSize: {
                              xs: 16,
                              sm: 17,
                            },
                            textDecoration:
                              'line-through',
                            textDecorationThickness:
                              '1.5px',
                          }}
                        >
                          {precioNormal}
                        </Typography>

                        {descuentoPorcentaje ? (
                          <Chip
                            label={`${descuentoPorcentaje}% OFF`}
                            size="small"
                            sx={{
                              bgcolor:
                                '#ecfdf5',
                              color:
                                '#047857',
                              fontWeight:
                                900,
                              border:
                                '1px solid #a7f3d0',
                            }}
                          />
                        ) : null}
                      </Stack>
                    ) : null}

                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      alignItems="baseline"
                    >
                      <Typography
                        sx={{
                          color:
                            colorPrimario,
                          fontWeight: 900,
                          fontSize: {
                            xs: 30,
                            sm: 34,
                            md: 38,
                          },
                          lineHeight: 1,
                          letterSpacing:
                            '-0.035em',
                        }}
                      >
                        {precio}
                      </Typography>

                      <Typography
                        component="span"
                        sx={{
                          color:
                            '#6b7280',
                          fontWeight: 700,
                          fontSize: {
                            xs: 16,
                            sm: 17,
                            md: 18,
                          },
                          lineHeight: 1.2,
                        }}
                      >
                        / {unidad}
                      </Typography>
                    </Stack>

                    {priceInfo.enOferta &&
                    ahorro ? (
                      <Typography
                        variant="body2"
                        sx={{
                          display:
                            'block',
                          mt: 0.75,
                          color:
                            '#047857',
                          fontWeight:
                            800,
                        }}
                      >
                        Ahorras {ahorro}
                      </Typography>
                    ) : null}

                    {producto.pesable ===
                    true ? (
                      <Typography
                        variant="caption"
                        sx={{
                          display:
                            'block',
                          mt: 0.75,
                          color:
                            '#9ca3af',
                        }}
                      >
                        Precio por{' '}
                        {unidad.toLowerCase()}.
                        El total puede
                        variar según el
                        peso final.
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}

                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  alignItems="center"
                >
                  <AvailableIcon
                    sx={{
                      color:
                        disponible
                          ? '#059669'
                          : '#9ca3af',
                      fontSize: 21,
                    }}
                  />

                  <Typography
                    sx={{
                      color:
                        disponible
                          ? '#047857'
                          : '#6b7280',
                      fontWeight: 800,
                    }}
                  >
                    {disponible
                      ? 'Disponible'
                      : 'Sin disponibilidad'}
                  </Typography>

                  {mostrarStock &&
                  stockKnown ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          '#6b7280',
                      }}
                    >
                      ·{' '}
                      {stockValue.toLocaleString(
                        'es-CL'
                      )}{' '}
                      en stock
                    </Typography>
                  ) : null}
                </Stack>

                {carritoHabilitado ? (
                  <Paper
                    elevation={0}
                    sx={{
                      mt: 0.5,
                      p: {
                        xs: 1.5,
                        sm: 2,
                      },
                      border:
                        '1px solid #e5e7eb',
                      borderRadius: 2.5,
                      bgcolor:
                        '#f8fafc',
                    }}
                  >
                    {tieneEnCarrito ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                          mb: 1.5,
                        }}
                      >
                        <CartIcon
                          sx={{
                            color:
                              colorPrimario,
                            fontSize: 20,
                          }}
                        />

                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              '#374151',
                            fontWeight:
                              800,
                          }}
                        >
                          Ya tienes{' '}
                          {cantidadEnCarrito.toLocaleString(
                            'es-CL'
                          )}{' '}
                          {cantidadEnCarrito ===
                          1
                            ? 'unidad'
                            : 'unidades'}{' '}
                          en el carrito
                        </Typography>
                      </Stack>
                    ) : null}

                    {puedeAgregar ? (
                      <Stack
                        direction={{
                          xs: 'column',
                          sm: 'row',
                        }}
                        spacing={1.25}
                        alignItems={{
                          xs: 'stretch',
                          sm: 'center',
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          sx={{
                            width: {
                              xs: '100%',
                              sm: 'auto',
                            },
                            justifyContent: {
                              xs: 'space-between',
                              sm: 'flex-start',
                            },
                            border:
                              '1px solid #d1d5db',
                            borderRadius: 2,
                            bgcolor:
                              '#fff',
                            overflow:
                              'hidden',
                          }}
                        >
                          <IconButton
                            onClick={
                              decreaseQuantity
                            }
                            disabled={
                              cantidadAgregar <=
                              1
                            }
                            aria-label="Disminuir cantidad"
                            sx={{
                              borderRadius: 0,
                            }}
                          >
                            <RemoveIcon />
                          </IconButton>

                          <Typography
                            sx={{
                              minWidth: 48,
                              px: 1,
                              textAlign:
                                'center',
                              color:
                                '#111827',
                              fontWeight:
                                900,
                              fontSize: 17,
                            }}
                          >
                            {cantidadAgregar}
                          </Typography>

                          <IconButton
                            onClick={
                              increaseQuantity
                            }
                            disabled={
                              maxAgregar !==
                                null &&
                              cantidadAgregar >=
                                maxAgregar
                            }
                            aria-label="Aumentar cantidad"
                            sx={{
                              borderRadius: 0,
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Stack>

                        <Button
                          variant="contained"
                          startIcon={
                            <CartIcon />
                          }
                          onClick={
                            agregarAlCarrito
                          }
                          sx={{
                            minHeight: 48,
                            px: 2.5,
                            bgcolor:
                              colorPrimario,
                            textTransform:
                              'none',
                            fontWeight:
                              900,
                            borderRadius: 2,

                            '&:hover': {
                              bgcolor:
                                colorPrimario,
                              filter:
                                'brightness(0.92)',
                            },
                          }}
                        >
                          {tieneEnCarrito
                            ? 'Agregar más'
                            : 'Agregar al carrito'}
                        </Button>

                        {tieneEnCarrito ? (
                          <Button
                            variant="outlined"
                            startIcon={
                              <CartIcon />
                            }
                            onClick={() =>
                              navigate(
                                '/carrito'
                              )
                            }
                            sx={{
                              minHeight: 48,
                              px: 2.25,
                              borderColor:
                                colorPrimario,
                              color:
                                colorPrimario,
                              textTransform:
                                'none',
                              fontWeight:
                                900,
                              borderRadius: 2,
                            }}
                          >
                            Ver carrito
                          </Button>
                        ) : null}
                      </Stack>
                    ) : (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              '#6b7280',
                            fontWeight:
                              700,
                          }}
                        >
                          {disponible
                            ? 'Ya agregaste la cantidad disponible al carrito.'
                            : 'Este producto no se encuentra disponible para comprar.'}
                        </Typography>

                        {tieneEnCarrito ? (
                          <Button
                            size="small"
                            onClick={() =>
                              navigate(
                                '/carrito'
                              )
                            }
                            sx={{
                              color:
                                colorPrimario,
                              textTransform:
                                'none',
                              fontWeight:
                                900,
                            }}
                          >
                            Ver carrito
                          </Button>
                        ) : null}
                      </Stack>
                    )}
                  </Paper>
                ) : null}

                {mercadolibre ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: {
                        xs: 1.5,
                        sm: 2,
                      },
                      border:
                        '1px solid #f3d54e',
                      borderRadius: 2.5,
                      bgcolor: '#fffdf3',
                    }}
                  >
                    <Stack
                      direction={{
                        xs: 'column',
                        sm: 'row',
                      }}
                      spacing={1.5}
                      alignItems={{
                        xs: 'stretch',
                        sm: 'center',
                      }}
                      justifyContent="space-between"
                    >
                      <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                      >
                        <Box
                          component="img"
                          src={mercadoLibreIcon}
                          alt="Mercado Libre"
                          sx={{
                            width: 34,
                            height: 34,
                            objectFit: 'contain',
                            flexShrink: 0,
                          }}
                        />

                        <Box>
                          <Typography
                            sx={{
                              color: '#1f2937',
                              fontWeight: 900,
                            }}
                          >
                            También disponible en Mercado Libre
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              color: '#6b7280',
                            }}
                          >
                            Si prefiere, puede comprar este producto en el canal oficial del marketplace.
                          </Typography>
                        </Box>
                      </Stack>

                      <Button
                        component="a"
                        href={
                          mercadolibre.url
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="contained"
                        endIcon={
                          <OpenInNewIcon />
                        }
                        sx={{
                          minHeight: 48,
                          px: 2.25,
                          bgcolor: '#ffe600',
                          color: '#2d3277',
                          textTransform: 'none',
                          fontWeight: 900,
                          borderRadius: 2,
                          whiteSpace: 'nowrap',
                          boxShadow: 'none',
                          '&:hover': {
                            bgcolor: '#f7dd00',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        Comprar
                      </Button>
                    </Stack>
                  </Paper>
                ) : null}

                {categorias.length >
                0 ? (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    flexWrap="wrap"
                  >
                    {categorias.map(
                      (
                        categoria,
                        index
                      ) => (
                        <Chip
                          key={
                            categoria?.id ??
                            categoria?.nombre ??
                            index
                          }
                          label={
                            categoria?.nombre ||
                            String(
                              categoria
                            )
                          }
                          size="small"
                          clickable={Boolean(
                            categoria?.slug
                          )}
                          component={
                            categoria?.slug
                              ? RouterLink
                              : 'div'
                          }
                          to={
                            categoria?.slug
                              ? `/categoria/${encodeURIComponent(
                                  String(
                                    categoria.slug
                                  ).trim()
                                )}`
                              : undefined
                          }
                          sx={{
                            bgcolor:
                              '#f3f4f6',
                            color:
                              '#4b5563',
                            textDecoration:
                              'none',

                            '&:hover': {
                              bgcolor:
                                categoria?.slug
                                  ? `${colorPrimario}12`
                                  : '#f3f4f6',
                              color:
                                categoria?.slug
                                  ? colorPrimario
                                  : '#4b5563',
                            },
                          }}
                        />
                      )
                    )}
                  </Stack>
                ) : null}

                {descripcion ? (
                  <>
                    <Divider />

                    <Box>
                      <Typography
                        sx={{
                          mb: 0.75,
                          color:
                            '#111827',
                          fontWeight:
                            800,
                        }}
                      >
                        Descripción
                      </Typography>

                      <Typography
                        sx={{
                          color:
                            '#4b5563',
                          lineHeight:
                            1.7,
                          whiteSpace:
                            'pre-line',
                        }}
                      >
                        {descripcion}
                      </Typography>
                    </Box>
                  </>
                ) : null}
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {compatibilidadFilas.length > 0 ? (
          <Paper
            component="section"
            elevation={0}
            aria-labelledby="compatibilidad-vehicular-titulo"
            sx={{
              mt: 2.5,
              border: '1px solid #e5e7eb',
              borderRadius: 3,
              bgcolor: '#fff',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: {
                  xs: 2,
                  sm: 2.5,
                  md: 3,
                },
                py: {
                  xs: 1.8,
                  sm: 2,
                },
                bgcolor: '#f8fafc',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <Stack
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                spacing={1.25}
                alignItems={{
                  xs: 'flex-start',
                  sm: 'center',
                }}
                justifyContent="space-between"
              >
                <Stack
                  direction="row"
                  spacing={1.15}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: `${colorPrimario}12`,
                      color: colorPrimario,
                      border: `1px solid ${colorPrimario}22`,
                      flexShrink: 0,
                    }}
                  >
                    <VehicleIcon
                      sx={{
                        fontSize: 21,
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      id="compatibilidad-vehicular-titulo"
                      component="h2"
                      sx={{
                        color: '#111827',
                        fontSize: {
                          xs: 18,
                          sm: 20,
                        },
                        fontWeight: 900,
                        lineHeight: 1.2,
                      }}
                    >
                      Compatibilidad
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.15,
                        color: '#64748b',
                        lineHeight: 1.4,
                      }}
                    >
                      Vehículos registrados para este producto
                    </Typography>
                  </Box>
                </Stack>

                <Typography
                  variant="caption"
                  sx={{
                    color: colorPrimario,
                    bgcolor: `${colorPrimario}0D`,
                    border: `1px solid ${colorPrimario}20`,
                    borderRadius: 999,
                    px: 1.15,
                    py: 0.45,
                    fontWeight: 900,
                    lineHeight: 1.2,
                  }}
                >
                  {compatibilidadTotal.toLocaleString(
                    'es-CL'
                  )}{' '}
                  {compatibilidadTotal === 1
                    ? 'aplicación'
                    : 'aplicaciones'}
                </Typography>
              </Stack>
            </Box>

            <Stack
              divider={
                <Divider
                  flexItem
                  sx={{
                    borderColor: '#eef2f7',
                  }}
                />
              }
            >
              {compatibilidadFilas.map(
                (item) => {
                  const detallesSecundarios = [
                    item.motor,
                    item.posicion
                      ? `Posición: ${item.posicion}`
                      : null,
                    item.cantidadPorVehiculo
                      ? `Cantidad por vehículo: ${item.cantidadPorVehiculo}`
                      : null,
                  ].filter(Boolean);

                  const observaciones = [
                    item.restricciones,
                    item.observaciones,
                  ].filter(Boolean);

                  return (
                    <Box
                      key={`${item.marca}-${item.modelo}-${item.key}`}
                      sx={{
                        px: {
                          xs: 2,
                          sm: 2.5,
                          md: 3,
                        },
                        py: {
                          xs: 1.5,
                          sm: 1.65,
                        },
                        transition:
                          'background-color 140ms ease',

                        '&:hover': {
                          bgcolor: '#fafcff',
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                        alignItems="baseline"
                      >
                        <Typography
                          component="span"
                          sx={{
                            color: '#111827',
                            fontSize: {
                              xs: 15.5,
                              sm: 16,
                            },
                            fontWeight: 900,
                            lineHeight: 1.4,
                          }}
                        >
                          {item.marca} {item.modelo}
                        </Typography>

                        {item.codigoModelo ? (
                          <Typography
                            component="span"
                            sx={{
                              color: '#475569',
                              fontSize: {
                                xs: 14.5,
                                sm: 15,
                              },
                              fontWeight: 700,
                              lineHeight: 1.4,
                            }}
                          >
                            {item.codigoModelo}
                          </Typography>
                        ) : null}

                        {item.anios ? (
                          <Typography
                            component="span"
                            sx={{
                              color: colorPrimario,
                              fontSize: {
                                xs: 14.5,
                                sm: 15,
                              },
                              fontWeight: 900,
                              lineHeight: 1.4,
                            }}
                          >
                            {item.anios}
                          </Typography>
                        ) : null}
                      </Stack>

                      {detallesSecundarios.length > 0 ? (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.35,
                            color: '#64748b',
                            fontSize: 13.5,
                            fontWeight: 650,
                            lineHeight: 1.45,
                          }}
                        >
                          {detallesSecundarios.join(
                            ' · '
                          )}
                        </Typography>
                      ) : null}

                      {observaciones.length > 0 ? (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.35,
                            color: '#7c8798',
                            lineHeight: 1.45,
                          }}
                        >
                          {observaciones.join(' · ')}
                        </Typography>
                      ) : null}
                    </Box>
                  );
                }
              )}
            </Stack>

            <Box
              sx={{
                px: {
                  xs: 2,
                  sm: 2.5,
                  md: 3,
                },
                py: 1.15,
                bgcolor: '#fafafa',
                borderTop: '1px solid #eef2f7',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#7c8798',
                  lineHeight: 1.45,
                }}
              >
                Verifique modelo, código y año del vehículo antes de comprar.
              </Typography>
            </Box>
          </Paper>
        ) : null}

        <Button
          component={RouterLink}
          to={categoriaNavegacionRuta}
          startIcon={
            <ArrowBackIcon />
          }
          sx={{
            mt: 2,
            color: '#6b7280',
            textTransform:
              'none',
            fontWeight: 800,
          }}
        >
          {categoriaNavegacionNombre
            ? `Volver a ${categoriaNavegacionNombre}`
            : 'Volver a categorías'}
        </Button>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2600}
        onClose={() =>
          setSnackbar(
            (current) => ({
              ...current,
              open: false,
            })
          )
        }
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }
          variant="filled"
          onClose={() =>
            setSnackbar(
              (current) => ({
                ...current,
                open: false,
              })
            )
          }
          sx={{
            width: '100%',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
